import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BehaviorSubject, catchError, EMPTY, filter, from, map, Observable, of, take, tap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { TokenService } from './token.service';
import { InteractionRequiredAuthError, RedirectRequest, SilentRequest } from '@azure/msal-browser';


@Injectable({
  providedIn: 'root'
})
export class TokenRefreshService {

  private refreshInProgress = false;
  private readonly refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly msal: MsalService,
    private readonly tokenService: TokenService,
    private readonly authService: AuthService,
  ) { }

  getValidAccessToken(scopes: string[]): Observable<string> {

    // 1️⃣ Token aún válido → usarlo
    if (this.tokenService.isTokenValid()) {
      return of(this.tokenService.getAccessToken()!);
    }

    // 2️⃣ Si ya hay refresh en curso → esperar
    if (this.refreshInProgress) {
      return this.refreshSubject.pipe(
        filter((token: any) => token !== null),
        take(1)
      );
    }

    // 3️⃣ Iniciar refresh
    this.refreshInProgress = true;
    this.refreshSubject.next(null);

    const account = this.msal.instance.getAllAccounts()[0];
    if (!account) {
      this.failAndLogout();
      return throwError(() => new Error('No account'));
    }

    const request: SilentRequest = { scopes, account };

    return from(this.msal.acquireTokenSilent(request)).pipe(
      tap(result => {
        this.tokenService.persist(result);
        this.refreshInProgress = false;
        this.refreshSubject.next(result.accessToken);
      }),
      map(result => result.accessToken),
      catchError(err => {
        this.refreshInProgress = false;

        if (err instanceof InteractionRequiredAuthError) {
          this.msal.acquireTokenRedirect(request as RedirectRequest);
          return EMPTY;
        }

        this.failAndLogout();
        return throwError(() => err);
      })
    );
  }

  private failAndLogout() {
    this.tokenService.clear();
    this.refreshSubject.complete();
    this.authService.logout();
  }
}
